<template>
    <div v-if="ledger" class="pl-6 p-2 h-full flex flex-col">
        <div class="grid grid-cols-8 gap-0 simple-gen-leg-inner-grid">
            <div class="simple-grid-row px-1">
                <div>Dato</div>
                <div>Bilagsnr.</div>
                <div>Beskrivelse</div>
                <div>Kunde/Leverandør</div>
                <div>Faktura Nr</div>
                <div>Vedlegg</div>
                <div>MVA</div>
                <div class="text-right">Beløp</div>
            </div>
            <div class="simple-grid-row bg-base-200">
                <div>{{ formattedDate(searchParams.fromDate) }}</div>
                <div/>
                <div>Inngående saldo</div>
                <div class="col-span-4"/>
                <div class="text-right font-bold">{{ formatCurrency(ledger.incomingBalance) }}</div>
            </div>
            <div v-for="tl in sortedRows" :key="tl.uuid" class="simple-grid-row">
                <div>{{ formattedDate(tl.date) }}</div>
                <transaction-link :transaction-uuid="tl.transactionUuid" class="text-primary">
                    {{ tl.transactionName }}
                </transaction-link>
                <div class="truncate">{{ tl.description }}</div>
                <gen-leg-sub-account-cell :params="{data: tl, context: params?.context}"/>
                <gen-leg-invoice-cell :params="{data: tl, context: params?.context}"/>
                <gen-leg-attachment-cell :params="{data: tl, context: params?.context}"/>
                <div>{{ getVatOptionById(tl.vatOptionId)?.code || '' }}</div>
                <div class="text-right">{{ formatCurrency(tl.amount) }}</div>
            </div>
            <div class="flex mt-2 items-baseline gap-2 justify-center col-span-full">
                <div>Viser <span class="text-lg">{{ sortedRows.length }}</span> av <span class="text-lg">{{ ledger.numTransactionLines }}</span> linjer</div>
                <a href="#" class="text-primary" @click="setCurrentAccountActive">Vis mer</a>
            </div>
            <div class="simple-grid-row bg-base-200">
                <div>{{ formattedDate(searchParams.toDate) }}</div>
                <div/>
                <div>Utgående saldo</div>
                <div class="col-span-4"/>
                <div class="text-right font-bold">{{ formatCurrency(ledger.outgoingBalance) }}</div>
            </div>
            <div class="simple-grid-row bg-base-200">
                <div class="col-span-2"/>
                <div>Endring i perioden</div>
                <div class="col-span-4"/>
                <div class="text-right">{{ formatCurrency(ledger.movement) }}</div>
            </div>
        </div>
        <div class="grow"/>
    </div>
    <div v-else/>
</template>

<script setup lang="ts">
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {computed, onMounted, ref, Ref} from 'vue'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {getTransactionLinesByIGetRowsParams} from '@/transaction/transaction.service'
import {IGetRowsParams} from 'ag-grid-community'
import {TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import TransactionLink from '@/transaction/TransactionLink.vue'
import {useVatService} from '@/vat/vat.service'
import GenLegAttachmentCell from '@/genleg/GenLegAttachmentCell.vue'
import GenLegSubAccountCell from '@/genleg/GenLegSubAccountCell.vue'
import GenLegInvoiceCell from '@/genleg/GenLegInvoiceCell.vue'

const {getVatOptionById} = useVatService()


const props = defineProps({
    params: {
        type: Object as () => {
            data: LedgerAccount,
            context: {
                panelId: Number,
                panelPathSegment: String,
                searchParams: Ref<{
                    fromDate: string,
                    toDate: string
                }>,
                setActiveAccount: Function,
                activeAccountNo: Ref<string>,
            }
        }
    }
})

const setCurrentAccountActive = () => {
    if (!props.params?.data.accountNo) return
    props.params.context.setActiveAccount(props.params.data.accountNo)
}

const ledger = computed<LedgerAccount | undefined>(() => props.params?.data)
const rows = ref<TransactionLine[]>([])


const sortedRows = computed(() => {
    return [...rows.value].sort((a, b) => a.date.localeCompare(b.date))
})
const searchParams = computed<TransactionSearchParams>(() => props.params!.context.searchParams.value)
const fetchData = async () => {
    const getRowsParams = {
        startRow: 0,
        endRow: 20,
        filterModel: {
            accountNo: {
                filterType: 'text',
                type: 'equals',
                filter: ledger.value?.accountNo || ''
            },
            date: {
                filterType: 'join',
                type: 'and',
                conditions: [
                    {
                        filterType: 'date',
                        type: 'lessThanOrEqual',
                        dateFrom: searchParams.value.toDate
                    },
                    {
                        filterType: 'date',
                        type: 'greaterThanOrEqual',
                        dateFrom: searchParams.value.fromDate
                    }
                ]
            }
        }
    }
    rows.value = await getTransactionLinesByIGetRowsParams(getRowsParams as IGetRowsParams)
}

onMounted(() => {
    fetchData()
})
</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";
.simple-gen-leg-inner-grid {
    grid-template-columns: 100px 100px 1fr 200px 90px 90px 90px 120px;
}

.simple-grid-row {
    @apply col-span-8 grid grid-cols-8;
    grid-template-columns: 100px 100px 1fr 200px 90px 90px 90px 120px;
    @apply gap-1 px-1 pt-1;
    height: 22px;
    margin-top: 1px;
    margin-bottom: 1px;
}


</style>